import { useEffect } from "react";
import { Map, useMap, InfoWindow } from "@vis.gl/react-google-maps";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { GeoPreviewMapProps, WARNING_THRESHOLD } from "./GeoPreviewMap.types";
import { useGeoPreviewMapViewModel } from "./GeoPreviewMap.vm";
import "./GeoPreviewMap.css";

// Child component to handle bounds fitting (useMap only works inside Map children)
function MapBoundsFitter({ coordinates }: { coordinates: google.maps.LatLngLiteral[] }) {
	const map = useMap();

	useEffect(() => {
		if (!map || coordinates.length === 0) return;

		const bounds = new google.maps.LatLngBounds();
		coordinates.forEach((coord) => bounds.extend(coord));

		// Use idle listener to ensure map is ready
		const idleListener = google.maps.event.addListenerOnce(map, "idle", () => {
			map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
		});

		// Also try immediately in case map is already idle
		map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });

		return () => {
			google.maps.event.removeListener(idleListener);
		};
	}, [map, coordinates]);

	return null;
}

export function GeoPreviewMap(props: GeoPreviewMapProps) {
	const vm = useGeoPreviewMapViewModel(props);

	// Loading state
	if (!vm.mapsLibrary) {
		return (
			<Box className="geo-preview-map-loading">
				<CircularProgress size={24} />
				<Typography variant="body2">Loading map...</Typography>
			</Box>
		);
	}

	// Empty state
	if (vm.processedFeatures.totalFeatures === 0) {
		return (
			<Box className="geo-preview-map-empty">
				<Typography variant="body2" color="text.secondary">
					No geodata available to display
				</Typography>
			</Box>
		);
	}

	return (
		<Box className="geo-preview-map">
			{vm.processedFeatures.totalFeatures > WARNING_THRESHOLD && (
				<Alert severity="warning">
					Displaying {vm.processedFeatures.totalFeatures} features. Map may be slow to interact with.
				</Alert>
			)}
			<Box className="map-container">
				<Map
					mapId="customerMaps"
					defaultCenter={{ lat: 40, lng: -95 }}
					defaultZoom={4}
					style={{ width: "100%", height: "100%" }}
					onClick={vm.handleMapClick}
				>
					<MapBoundsFitter coordinates={vm.processedFeatures.allCoordinates} />
					{vm.processedFeatures.polygonElements}
					{vm.processedFeatures.pointElements}
					{vm.activePopup && (
						<InfoWindow
							position={vm.activePopup.position}
							onCloseClick={() => vm.setActivePopup(null)}
							headerContent="Properties"
						>
							<Box className="popup-content">
								{Object.keys(vm.activePopup.properties).length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No properties
									</Typography>
								) : (
									Object.entries(vm.activePopup.properties).map(([key, value]) => (
										<Typography key={key} variant="body2">
											<strong>{key}:</strong> {String(value ?? "")}
										</Typography>
									))
								)}
							</Box>
						</InfoWindow>
					)}
				</Map>
			</Box>
		</Box>
	);
}
